import { FC, ReactNode } from 'react';

import { Button } from '@new/common/ui';
import { CancelButton } from '@new/common/ui/CancelButton/CancelButton';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  children?: ReactNode;
  confirmDisabled?: boolean;
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  onClose,
  onConfirm,
  children,
  confirmDisabled = false,
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isLoading) {
      onClose();
    }
  };

  const handleContentClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-999 flex items-center justify-center"
      onClick={handleContentClick}
      onMouseDown={handleContentClick}
    >
      <div
        className="fixed inset-0 bg-(--neutral-black-NB1000)/50"
        onClick={handleBackdropClick}
        onMouseDown={handleBackdropClick}
      />

      <div
        className={`relative z-10 flex max-w-xs md:max-w-md lg:max-w-lg flex-col gap-6 rounded-3xl bg-(--neutral-white-NW1000) p-6 shadow-xl`}
        onClick={handleContentClick}
        onMouseDown={handleContentClick}
      >
        <div className="flex flex-col gap-2">
          <h3 className="m-0! [font-size:var(--fs-l2)]! lg:[font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB1000)!">
            {title}
          </h3>

          {description && (
            <p className="m-0! [font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! leading-[150%] text-(--neutral-black-NB700)!">
              {description}
            </p>
          )}
        </div>

        {children}

        <div className="flex justify-end gap-3">
          <CancelButton onClick={onClose} disabled={isLoading} text={cancelText} />
          <Button variant="primary" onClick={onConfirm} disabled={isLoading || confirmDisabled}>
            {isLoading ? 'Submitting...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};
